import React from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * DateModeSelector — toggle Origine / Attività per i filtri KPI.
 *
 *   • Origine   → filtro su `created_at` del lead (quando è entrato nel CRM)
 *   • Attività  → filtro sulle transizioni di stage (quando è stato lavorato)
 *
 * Default: `activity` (definito nel backend). L'utente può cambiare da UI.
 *
 * Props:
 *   value:     'origin' | 'activity'
 *   onChange:  (mode) => void
 *   size:      'sm' | 'md'  (default 'md')
 *   testIdPrefix: string    (default 'date-mode')
 */
export function DateModeSelector({ value = 'activity', onChange, size = 'md', testIdPrefix = 'date-mode' }) {
  const { t } = useTranslation();
  const paddingCls = size === 'sm' ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs';
  const isOrigin = value === 'origin';
  const isActivity = value === 'activity';

  return (
    <TooltipProvider delayDuration={200}>
      <div className="inline-flex items-center gap-1.5" data-testid={testIdPrefix}>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium hidden sm:inline">
          {t('leads.dateMode.label', 'Filtra per')}
        </span>
        <div
          className="inline-flex rounded-full border border-border bg-[var(--surface-2)] p-0.5"
          role="radiogroup"
          aria-label={t('leads.dateMode.radioGroupAria', 'Modalità filtro date')}
        >
          <button
            type="button"
            role="radio"
            aria-checked={isOrigin}
            onClick={() => onChange?.('origin')}
            data-testid={`${testIdPrefix}-origin`}
            className={`${paddingCls} rounded-full font-medium transition-colors ${
              isOrigin
                ? 'bg-[hsl(var(--primary))] text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('leads.dateMode.origin', 'Origine')}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={isActivity}
            onClick={() => onChange?.('activity')}
            data-testid={`${testIdPrefix}-activity`}
            className={`${paddingCls} rounded-full font-medium transition-colors ${
              isActivity
                ? 'bg-[hsl(var(--primary))] text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('leads.dateMode.activity', 'Attività')}
          </button>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              aria-label={t('leads.dateMode.helpAria', 'Cosa significa Origine/Attività?')}
              data-testid={`${testIdPrefix}-help`}
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
            <p className="mb-1"><b>{t('leads.dateMode.helpOriginTitle', 'Origine')}</b>: {t('leads.dateMode.helpOriginBody', 'conta i lead ENTRATI nel CRM nel periodo (data creazione).')}</p>
            <p><b>{t('leads.dateMode.helpActivityTitle', 'Attività')}</b>: {t('leads.dateMode.helpActivityBody', 'conta i lead LAVORATI nel periodo (qualsiasi cambio di stage, anche se entrati prima).')}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

export default DateModeSelector;
